import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ConvertISONumberToNormal, getFormData } from 'src/app/shared/helpers/utils';
import { Investment } from 'src/app/shared/interfaces/investments-response.interface';
import Swal from 'sweetalert2';
import { InvestmentsHttpService } from '../../common/services/investments-http.service';
import { InvestmentsFormProvider } from '../../investments-form-provider';
import { NgbActiveModal, NgbDate, NgbDatepickerConfig, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { INVESTOR_ID } from 'src/app/shared/enums/common.enum';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { FinancialStatementHttpService } from 'src/app/financial-statement/common/services/financial-statement-http.service';
import { SpinnerService } from '../../../shared/services/spinner.service';
import { MasterDataHttpService } from '../../../shared/services/master-data-http.service';
import { CCY_SORT_ORDER } from "../../../shared/constants/helpers.const";
import { SimpleNumberValueFormatter } from "../../../shared/helpers/number-formatter.helper";
import { formatSimpleNumber } from "../../../shared/helpers/v2";

@Component({
  selector: "app-add-edit-investments",
  templateUrl: "./add-edit-investments.component.html",
  styleUrls: ["./add-edit-investments.component.scss"]
})
export class AddEditInvestmentsComponent implements OnInit {
  investmentForm: FormGroup;
  private changeSubscription: Subscription = new Subscription();
  isSubmitted = false;
  investorId: string;
  type: { id: string; name: string }[];
  investors: { id: string; name: string }[];
  allCcyData: { id: string; name: string }[];
  transactionType: { id: string; name: string }[];
  product: { id: string; name: string }[];
  investmentData: Investment;
  financialStatementData: any[] = [];
  currentDate = moment().format("YYYY-MM-DD");
  private queryParams: Partial<any> = {
    pageIndex: 0,
    perPage: 10000,
    investorId: INVESTOR_ID
  };
  today = new Date();

  constructor(
    private investmentsHttpService: InvestmentsHttpService,
    private investmentsFormProvider: InvestmentsFormProvider,
    public activeModal: NgbActiveModal,
    private config: NgbDatepickerConfig,
    private financialStatementHttpService: FinancialStatementHttpService,
    private spinnerService: SpinnerService,
    private masterDataHttpService: MasterDataHttpService
  ) {
    // this.initDatePickerConfig();
    this.initializeIntermediaryId();
  }

  ngOnInit(): void {
    this.getFinancialStatementByInvestorId(this.generateRequestList());
    this.investmentForm = this.investmentsFormProvider.initInvestmentForm(this.investmentData);
    this.getDropDownsData();
  }

  initializeIntermediaryId(): void {
    const storedData = localStorage.getItem("userData");
    const userDetails = storedData ? JSON.parse(storedData) : {};
    this.investorId = userDetails.entityRoleId || null;
  }

  onSelect(event: any): void {
    this.isSubmitted = false;
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.investmentForm.invalid) {
      return;
    }
    let rawData = this.investmentForm.value;
    rawData = getFormData(rawData);
    rawData.investorId = this.investorId?.toString();
    rawData.months = Number(rawData.months);
    if (rawData.transactionType === "Receipt") {
      rawData.faceValue = Number(rawData.faceValue.replace(/,/g, ""));
    } else {
      rawData.faceValue = null;
    }
    delete rawData.intermediaryName;
    this.doCreate(rawData);
  }

  doCreate(data: any): void {
    this.spinnerService.setShowSpinner(true);
    this.investmentsHttpService.postData("investments", data).subscribe({
      next: (response: any) => {
        this.closeModal();
        this.spinnerService.setShowSpinner(false);
        this.investmentForm.reset();
        Swal.fire({
          icon: "success",
          text: "Investment successfully added.",
          showConfirmButton: false,
          timer: 5000
        });
      },
      error: (error) => {
        this.spinnerService.setShowSpinner(false);
        const errorMessages = error.error.message;
        const containsFutureDateError = Array.isArray(errorMessages) ? errorMessages.some((msg: string) => msg.includes("maximal allowed date for")) : typeof errorMessages === "string" && errorMessages.includes("maximal allowed date for");

        const defaultMessage = containsFutureDateError ? "Error" : Array.isArray(errorMessages) ? errorMessages.join(", ") : errorMessages;
        Swal.fire({
          icon: "error",
          text: defaultMessage,
          showConfirmButton: true,
          confirmButtonText: "Ok"
        });
      }
    });
  }

  private getDropDownsData(): void {
    this.investmentsHttpService.getInvestmentsFilterData().subscribe((investmentsFilterData) => {
      investmentsFilterData.types.sort((a, b) => a.localeCompare(b));
      this.type = [
        ...(investmentsFilterData.types.map((type) => ({
          id: type,
          name: type
        })) || [])
      ];

      let foundInvestor: any = null;
      let foundIntermediary: any = null;
      for (let intermediary of investmentsFilterData.intermediariesWithInvestors) {
        let investor = intermediary.investors.find((investor: any) => investor.id == this.investorId);
        if (investor) {
          foundInvestor = investor;
          foundIntermediary = intermediary;
          break;
        }
      }
      if (!this.investors) {
        this.investors = [];
      }
      if (foundInvestor && foundIntermediary) {
        this.investors.push({
          id: foundIntermediary.id,
          name: foundIntermediary.name
        });
      }
      this.investors.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      this.investmentForm.get("intermediaryId")?.setValue(this.investors[0]?.id);
      this.investmentForm.get("intermediaryName")?.setValue(this.investors[0]?.name);
      this.transactionType = [
        ...(investmentsFilterData.transactionTypes
          .filter((transactionType) => !["Redemption", "Adjustment", "Renewal"].includes(transactionType))
          .map((transactionType) => ({
            id: transactionType,
            name: transactionType
          })) || [])
      ];
      this.product = [
        ...(investmentsFilterData.products.map((product) => ({
          id: product,
          name: product
        })) || [])
      ];
    });

    this.masterDataHttpService.getCurrencyList().subscribe((response: any) => {
      let orderMap: any = new Map(CCY_SORT_ORDER.map((ccy, index) => [ccy, index]));
      this.allCcyData = response
        .sort((a: any, b: any) => orderMap.get(a) - orderMap.get(b))
        .map((item: any) => {
          return { id: item, name: item };
        });
    });
  }

  closeModal() {
    this.activeModal.dismiss();
  }

  private getCurrentDate(): NgbDate {
    const today = new Date();
    return new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
  }

  initDatePickerConfig() {
    const currentDate = this.getCurrentDate();
    this.config.minDate = { year: 1970, month: 1, day: 1 };
    this.config.maxDate = currentDate;
  }

  setPrePopulatedValue() {
    const data = localStorage.getItem("financialStatementData");
    if (data) {
      this.financialStatementData = JSON.parse(data);
      if (this.financialStatementData && this.financialStatementData.length > 0) {
        this.investmentForm.get("date")?.setValue(this.currentDate);
        const ccy = this.getUniqueValue(this.financialStatementData, "ccy") ?? "";
        const type = this.getUniqueValue(this.financialStatementData, "type") ?? "";
        if (type === "Multiple") {
          this.investmentForm.get("type")?.setValue("");
        } else {
          const months = type === "Demand" || type === "Fixed" ? 12 : type === "Term" ? 36 : null;
          if (months !== null) this.investmentForm.get("months")?.setValue(months);
          this.investmentForm.get("type")?.setValue(type);
        }
        this.investmentForm.get("ccy")?.setValue(ccy === "Multiple" ? "" : ccy);
      }
    }
  }

  handleFocusFaceValue(): void {
    if (isNaN(+this.investmentForm.value.faceValue)) {
      this.investmentForm.get("faceValue")?.patchValue("");
      return;
    }
    this.investmentForm.get("faceValue")?.patchValue(ConvertISONumberToNormal(+this.investmentForm.value.faceValue));
  }

  handleFocusOutFaceValue(): void {
    if (isNaN(+this.investmentForm.value.faceValue)) {
      this.investmentForm.get("faceValue")?.patchValue("");
      return;
    }
    this.investmentForm.get("faceValue")?.patchValue(formatSimpleNumber(this.investmentForm.value.faceValue));
  }

  getUniqueValue(data: any[], key: string) {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.length > 1 ? "Multiple" : uniqueValues[0];
  }

  onTypeChange(event: any) {
    const selectedValue = event.target.value;
    const months = selectedValue === "Demand" || selectedValue === "Fixed" ? 12 : selectedValue === "Term" ? 36 : null;
    this.investmentForm.get("months")?.setValue(selectedValue ? months : null);
  }

  private generateRequestList(): Record<string, Observable<any>> {
    let requestList: Record<string, Observable<any>> = {};
    requestList = {
      demand: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Demand"
      }),
      fixed: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Fixed"
      }),
      term: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Term"
      })
    };
    return requestList;
  }

  private getFinancialStatementByInvestorId(requestList: Record<string, Observable<any>>): void {
    forkJoin(requestList).subscribe((data) => {
      let rowData: any = [];
      for (const dataKey in data) {
        switch (dataKey) {
          case "demand":
          case "fixed":
          case "term":
            rowData = [...rowData, ...this.filterReceiptAmountsGrid(data[dataKey].items)];
            break;
        }
      }
      localStorage.setItem("financialStatementData", JSON.stringify(rowData));
      if (this.investorId && rowData?.length) {
        this.investmentForm.get("investorId")?.setValue(this.investorId);
        this.setPrePopulatedValue();
      }
    });
  }

  filterReceiptAmountsGrid(data: any[]): any[] {
    const filteredData: any[] = [];
    const seenGroups = new Map<string, any[]>();
    // Group data by type and currency
    data.forEach((item) => {
      const key = `${item.type}-${item.ccy}`;
      if (!seenGroups.has(key)) {
        seenGroups.set(key, []);
      }
      seenGroups.get(key)?.push(item);
    });
    // Process each group
    seenGroups.forEach((entries) => {
      let lastZeroIndex = -1;
      // Find the last index where balance is 0 or null
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].balance === 0 || entries[i].balance === null) {
          lastZeroIndex = i;
        }
      }
      // Include only rows after the last zero/null balance
      if (lastZeroIndex !== -1 && lastZeroIndex < entries.length - 1) {
        filteredData.push(...entries.slice(lastZeroIndex + 1));
      } else if (lastZeroIndex === -1) {
        // If no zero/null balance found, include all rows
        filteredData.push(...entries);
      }
    });
    return filteredData;
  }

  ngOnDestroy(): void {
    this.changeSubscription.unsubscribe();
  }
}
